import type {
  GetHistoryClassesItem,
  GetHistoryParams,
  GetHistoryPopulationTypeItem,
  GetHistoryTouchpointContentTypeItem,
  GetHistoryTouchpointTypeItem,
  GetHistoryTouchpointVersionStatusItem,
} from "generated/schemas";

import { PaginatedRequestOptions } from "types/pagination";
import { InboxFilter } from "types/inbox";

type HistoryCriteria = Pick<
  GetHistoryParams,
  | "touchpointVersionStatus"
  | "touchpointType"
  | "touchpointContentType"
  | "populationType"
  | "classes"
  | "clientId"
  | "hasOpenComments"
  | "hasOpenActionComments"
  | "q"
>;

const toStringArray = (value: InboxFilter[keyof InboxFilter]): string[] | undefined => {
  if (!Array.isArray(value) || value.length === 0) {
    return undefined;
  }
  return value.map(String);
};

export function mapInboxFilterToCriteria(filters?: InboxFilter): HistoryCriteria {
  if (!filters) return {};

  const criteria: HistoryCriteria = {};

  const status = toStringArray(filters.touchpointVersionStatus);
  if (status) {
    criteria.touchpointVersionStatus = status as GetHistoryTouchpointVersionStatusItem[];
  }

  const type = toStringArray(filters.touchpointType);
  if (type) {
    criteria.touchpointType = type as GetHistoryTouchpointTypeItem[];
  }

  const contentType = toStringArray(filters.touchpointContentType);
  if (contentType) {
    criteria.touchpointContentType = contentType as GetHistoryTouchpointContentTypeItem[];
  }

  const populationType = toStringArray(filters.populationType);
  if (populationType) {
    criteria.populationType = populationType as GetHistoryPopulationTypeItem[];
  }

  const classes = toStringArray(filters.classes);
  if (classes) {
    criteria.classes = classes as GetHistoryClassesItem[];
  }

  const clientId = toStringArray(filters.clientId);
  if (clientId) {
    criteria.clientId = clientId;
  }

  if (typeof filters.hasOpenComments === "boolean") {
    criteria.hasOpenComments = filters.hasOpenComments;
  }
  if (typeof filters.hasOpenActionComments === "boolean") {
    criteria.hasOpenActionComments = filters.hasOpenActionComments;
  }
  if (filters.q) {
    criteria.q = filters.q;
  }

  return criteria;
}

/**
 * Legacy callers serialize multiple Spring `sort` params by joining with the
 * literal `"&sort="` separator (e.g. `"a,desc&sort=b,asc"`), which is then
 * appended to the URL as `?sort=...&sort=...`. Split that representation back
 * into the array shape Orval's `Pageable.sort` expects.
 */
export function normalizeSort(sort: string | undefined): string[] | undefined {
  if (!sort) return undefined;
  const parts = sort.split("&sort=").filter(Boolean);
  return parts.length ? parts : undefined;
}

export function buildHistoryParams(
  options: PaginatedRequestOptions,
  filters?: InboxFilter,
): GetHistoryParams {
  const { page, size, sort } = options;
  const sortArray = normalizeSort(sort);
  return {
    ...mapInboxFilterToCriteria(filters),
    ...(page !== undefined && { page }),
    ...(size !== undefined && { size }),
    ...(sortArray && { sort: sortArray }),
  };
}
